import { NOTIFYABLE_ERROR_CODES } from '@constants'
import { i18nInstance } from '@localisation'
import { getApiErrorMessage } from './getApiErrorMessage'
import { getAppMarket, toMarketHeader } from './marketHost'
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import qs from 'qs'
import { toast } from 'react-hot-toast'
import { CLIENT_MUTATION_ID_HEADER, clientMutationIdRegistry } from './clientMutationIdRegistry'

export const BASE_URL = import.meta.env.VITE_NOCFO_BASE_URL

if (import.meta.env.DEV && !BASE_URL) {
  console.error(
    '[NOCFO] VITE_NOCFO_BASE_URL is not set. ' +
      'Copy config/.env.local to config/.env to configure local development.'
  )
}

const DEFAULT_TIMEOUT = import.meta.env?.DEFAULT_TIMEOUT || 10000 // 10s
const MUTATING_METHODS = new Set(['post', 'patch', 'put', 'delete'])
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json;charset=UTF-8',
  'X-NOCFO-CLIENT': `nocfo-web/${process.env.SENTRY_RELEASE || 'dev'}`
}

const DEFAULT_HTTP_CLIENT_CONFIG: AxiosRequestConfig = {
  baseURL: BASE_URL,
  headers: DEFAULT_HEADERS,
  timeout: DEFAULT_TIMEOUT,
  xsrfCookieName: import.meta.env.VITE_CSRF_COOKIE_NAME,
  xsrfHeaderName: 'X-CSRFToken',
  withXSRFToken: true,
  withCredentials: true,
  paramsSerializer: params => qs.stringify(params, { indices: false }),
  validateStatus: status => {
    return status >= 200 && status < 300
  }
}

const createToastId = (message: string) => `error-toast-${encodeURIComponent(message)}`

const showErrorToast = (message: string) => {
  const normalizedMessage = message.trim() || i18nInstance.t('general.error')
  toast.error(normalizedMessage, { id: createToastId(normalizedMessage) })
}

class Networking {
  httpClient: AxiosInstance
  private bearerToken?: string

  constructor() {
    this.httpClient = axios.create(DEFAULT_HTTP_CLIENT_CONFIG)
    this.httpClient.interceptors.request.use(this.requestInterceptor)
    this.httpClient.interceptors.response.use(
      this.responseInterceptor,
      this.responseErrorInterceptor
    )
  }

  private requestInterceptor = (config: InternalAxiosRequestConfig) => {
    const headers =
      config.headers instanceof AxiosHeaders
        ? config.headers
        : new AxiosHeaders(config.headers || {})

    headers.set('X-NoCFO-Market', toMarketHeader(getAppMarket()))

    if (this.bearerToken) {
      headers.set('Authorization', `Bearer ${this.bearerToken}`)
      config.withCredentials = false
      config.withXSRFToken = false
    } else {
      headers.delete('Authorization')
      config.withCredentials = true
      config.withXSRFToken = true
    }

    const method = config.method?.toLowerCase()
    if (method && MUTATING_METHODS.has(method)) {
      const existingMutationId = headers.get(CLIENT_MUTATION_ID_HEADER)
      if (existingMutationId) {
        clientMutationIdRegistry.registerClientMutationId(String(existingMutationId))
      } else {
        headers.set(
          CLIENT_MUTATION_ID_HEADER,
          clientMutationIdRegistry.createAndRegisterClientMutationId()
        )
      }
    }

    config.headers = headers
    return config
  }

  private responseInterceptor = (response: AxiosResponse<any>) => {
    // Need to handle the error in all cases as
    // in many places we are overriding the
    // validateStatus to always return true which
    // skips the responseErrorInterceptor.
    if (
      response.status === 403 &&
      typeof response.data?.message === 'string' &&
      response.data.message.trim()
    ) {
      showErrorToast(response.data.message)
    }

    return response
  }

  private responseErrorInterceptor = (error: AxiosError<any>) => {
    // CASE: Predefined backend error cases that somewhat unrelated to users current actions
    if (NOTIFYABLE_ERROR_CODES.includes(error.response.status)) {
      if (error.response.status === 428) {
        showErrorToast(i18nInstance.t('error.upgradeRequired'))
      } else if (error.response.status === 403) {
        const forbiddenTranslation = i18nInstance.t('error.forbidden')
        const forbiddenMessage = error.response.data?.message || forbiddenTranslation

        showErrorToast(forbiddenMessage)
      } else if (error.response.status === 423) {
        showErrorToast(getApiErrorMessage(error, i18nInstance.t('error.oopsTitle')))
      } else {
        showErrorToast(i18nInstance.t('error.oopsTitle'))
      }
      throw error
    }
    throw error
  }

  setBearerToken(token?: string) {
    this.bearerToken = token || undefined
  }

  getBearerToken(): string | undefined {
    return this.bearerToken
  }
}

export const network = new Networking()
